import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import Seo from '@/components/Seo';
import Starfield from '@/components/Starfield';
import { GrainOverlay } from '@/components/Overlays';
import Footer from '@/components/Footer';
import WaitlistForm from '@/components/WaitlistForm';
import { useWaitlistCount } from '@/hooks/useWaitlistCount';

const DEADLINE_KEY = 'dumbverse_alley_deadline';
const OFFER_MS = 10 * 60 * 1000;

const getDeadline = () => {
  try {
    const stored = Number(localStorage.getItem(DEADLINE_KEY));
    if (stored && stored > Date.now()) return stored;
    if (stored) return stored; // expired — keep it so the page can admit it
    const fresh = Date.now() + OFFER_MS;
    localStorage.setItem(DEADLINE_KEY, String(fresh));
    return fresh;
  } catch {
    return Date.now() + OFFER_MS;
  }
};

const facts = [
  'A shrimp\'s heart is in its head. Yours is in a spreadsheet.',
  'You have had 4 good ideas today and posted zero of them.',
  'The word "dumb" appears 41 times on this page. Now 42.',
  'Somewhere, a nine-member AI council is arguing about your idea already.',
  'This page has no purpose. Neither did the wheel, at first.',
];

const testimonials = [
  ['"I clicked one button and now I have ideas. Unbelievable."', '— D███ M., verified* explorer'],
  ['"My cousin\'s dentist made 4 ideas in ONE WEEK from home."', '— anonymous, probably real'],
  ['"I was skeptical. Then I was still skeptical. Then I joined."', '— user #0███7'],
];

const Box = ({ children, accent = false }: { children: React.ReactNode; accent?: boolean }) => (
  <div
    className={`mx-auto my-4 max-w-2xl rounded-lg border bg-card/40 p-6 backdrop-blur-sm ${
      accent ? 'border-primary/40' : 'border-border'
    }`}
  >
    {children}
  </div>
);

const Tag = ({ children }: { children: React.ReactNode }) => (
  <span className="mb-3 inline-block border border-dashed border-border px-2 py-1 font-mono text-[11px] uppercase tracking-[0.18em] text-muted-foreground">
    {children}
  </span>
);

const Dumb = () => {
  const [factIndex, setFactIndex] = useState(0);
  const [clicks, setClicks] = useState(0);
  const [idea, setIdea] = useState('');
  const [verdict, setVerdict] = useState<string | null>(null);
  const [deadline, setDeadline] = useState(() => getDeadline());
  const [remaining, setRemaining] = useState(() => Math.max(0, deadline - Date.now()));
  const [extensions, setExtensions] = useState(0);
  const [popup, setPopup] = useState(false);
  const [dismissals, setDismissals] = useState(0);
  const [drift, setDrift] = useState(0);
  const { count } = useWaitlistCount();
  const viewers = Math.max(11, Math.round(count / 40) + 23 + drift);

  useEffect(() => {
    const t = setInterval(() => setFactIndex(i => (i + 1) % facts.length), 3500);
    return () => clearInterval(t);
  }, []);

  useEffect(() => {
    const t = setInterval(() => setRemaining(Math.max(0, deadline - Date.now())), 250);
    return () => clearInterval(t);
  }, [deadline]);

  useEffect(() => {
    const t = setInterval(() => setDrift(d => Math.max(-9, Math.min(9, d + (Math.random() > 0.5 ? 1 : -1)))), 2200);
    return () => clearInterval(t);
  }, []);

  const extendOffer = () => {
    const fresh = Date.now() + OFFER_MS;
    try { localStorage.setItem(DEADLINE_KEY, String(fresh)); } catch { /* ignore */ }
    setDeadline(fresh);
    setRemaining(OFFER_MS);
    setExtensions(e => e + 1);
  };

  useEffect(() => {
    const t = setTimeout(() => setPopup(true), 6000);
    return () => clearTimeout(t);
  }, []);

  const rate = () => {
    if (!idea.trim()) return;
    const score = (idea.trim().length * 7) % 101;
    setVerdict(
      `DUMBNESS SCORE: ${score}/100. ${
        score > 66
          ? 'Magnificent. Post it immediately.'
          : score > 33
            ? 'Suspiciously reasonable. Make it dumber.'
            : 'Too sensible. The council will be bored.'
      }`
    );
  };

  const totalSeconds = Math.ceil(remaining / 1000);
  const expired = remaining <= 0;
  const mm = String(Math.floor(totalSeconds / 60)).padStart(2, '0');
  const ss = String(totalSeconds % 60).padStart(2, '0');

  const shareText = encodeURIComponent(
    'I found the dumbest page on the internet and I cannot un-see it → https://dumbverse.app/dumb'
  );

  return (
    <div className="relative min-h-screen bg-background">
      <Seo
        title="The Dumbest Page On The Internet | Dumbverse"
        description="A deliberately useless webpage with a dumb idea rater, redacted facts and one working button. Built by Dumbverse. Do not show your boss."
        path="/dumb"
      />
      <Starfield />
      <GrainOverlay />

      {popup && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-background/80 p-4 backdrop-blur-sm" role="dialog" aria-label="Suspicious offer">
          <div className="w-full max-w-sm rounded-lg border border-primary/40 bg-card p-6 text-center">
            <p className="font-mono text-xs tracking-[0.16em]" style={{ color: 'hsl(var(--hype))' }}>⚠ UNSOLICITED OFFER ⚠</p>
            <h2 className="my-2 font-bebas text-3xl text-foreground">PSST. OVER HERE.</h2>
            <p className="font-mono text-sm leading-relaxed text-foreground/80">
              A guy in an alley told us you have an idea. He wants nothing. The nine inhabitants want to hear it.
            </p>
            <button
              className="mt-4 rounded border border-border bg-muted px-4 py-2 font-mono text-sm text-foreground hover:bg-accent transition-colors"
              onClick={() => { setPopup(false); setDismissals(d => d + 1); }}
            >
              {dismissals === 0 ? 'no thanks' : dismissals === 1 ? 'still no' : 'fine, close it'}
            </button>
            <p className="mt-3 font-mono text-xs text-muted-foreground">
              {dismissals === 0 ? 'This dialog is legally optional.' : 'It will probably come back.'}
            </p>
          </div>
        </div>
      )}

      <main className="relative z-10 px-6 py-20 text-center">
        <p className="font-mono text-xs tracking-[0.3em] uppercase text-muted-foreground">
          <Link to="/" className="hover:text-foreground transition-colors">Dumbverse</Link> / The dumbest page
        </p>
        <h1 className="mx-auto mt-4 max-w-3xl font-bebas text-5xl md:text-7xl leading-none text-foreground">
          THE DUMBEST PAGE ON THE INTERNET
        </h1>
        <p className="mt-4 font-playfair italic text-xl text-muted-foreground">
          Nothing here is useful. That is the entire point.
        </p>
        <p className="mt-4 font-mono text-sm animate-pulse" style={{ color: 'hsl(var(--hype))' }}>
          ● {viewers} strangers are lurking on this page right now
        </p>
        <p className="mt-1 font-mono text-xs text-muted-foreground">
          Based on {count.toLocaleString()} real explorers. The rest we made up.
        </p>

        <Box accent>
          <div className="text-center">
            <Tag>{expired ? 'Offer expired (allegedly)' : 'Offer expires for real'}</Tag>
            <div
              className={`font-bebas text-5xl tracking-widest ${expired ? '' : totalSeconds < 60 ? 'animate-pulse' : ''}`}
              style={{ color: 'hsl(var(--hype))' }}
              aria-live="off"
            >
              {expired ? '00:00' : `${mm}:${ss}`}
            </div>
            {expired ? (
              <>
                <p className="mt-2 font-mono text-xs text-muted-foreground">
                  It genuinely ran out. It does not restart on its own. You have to press this.
                </p>
                <button
                  onClick={extendOffer}
                  className="mt-3 rounded border border-primary/40 bg-muted px-5 py-2 font-mono text-sm text-foreground hover:bg-accent transition-colors"
                >
                  extend the offer myself
                </button>
                {extensions > 0 && (
                  <p className="mt-2 font-mono text-xs text-muted-foreground">
                    Extended {extensions} time{extensions === 1 ? '' : 's'} by you. Nobody else is involved.
                  </p>
                )}
              </>
            ) : (
              <p className="mt-2 font-mono text-xs text-muted-foreground">
                This one keeps ticking even if you refresh. Suspicious, but honest.
              </p>
            )}
          </div>
        </Box>

        <Box>
          <div className="text-left">
            <Tag>Unlicensed tool</Tag>
            <h2 className="font-bebas text-3xl text-foreground">🧠 DUMB IDEA RATER 3000</h2>
            <p className="font-mono text-xs text-muted-foreground">Powered by absolutely nothing. Results are legally meaningless.</p>
            <input
              value={idea}
              onChange={e => { setIdea(e.target.value); setVerdict(null); }}
              placeholder="whisper your dumbest idea here"
              aria-label="Your dumbest idea"
              className="mt-3 w-full rounded border border-input bg-secondary px-3 py-2 font-mono text-sm text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-ring"
            />
            <button
              onClick={rate}
              className="mt-3 rounded bg-primary px-5 py-2 font-bebas text-lg tracking-wide text-primary-foreground hover:bg-primary/90 transition-colors"
            >
              RATE IT
            </button>
            {verdict && <p className="mt-4 font-mono text-sm" style={{ color: 'hsl(var(--hype))' }}>{verdict}</p>}
          </div>
        </Box>

        <Box>
          <div className="text-left">
            <Tag>Leaked file {factIndex + 1}/{facts.length}</Tag>
            <h2 className="font-bebas text-3xl text-foreground">📜 REDACTED FACT OF THE MOMENT</h2>
            <p className="mt-2 font-mono text-sm leading-relaxed text-foreground/80">{facts[factIndex]}</p>
            <p className="mt-2 font-mono text-xs text-muted-foreground">Source: a man in a van · Verified by nobody</p>
          </div>
        </Box>

        <Box>
          <div className="text-left">
            <Tag>Unsupervised button</Tag>
            <h2 className="font-bebas text-3xl text-foreground">🔘 THE ONLY BUTTON THAT WORKS</h2>
            <button
              onClick={() => setClicks(c => c + 1)}
              className="mt-2 rounded border border-border bg-muted px-5 py-2 font-mono text-sm text-foreground hover:bg-accent transition-colors"
            >
              DO NOT PRESS ({clicks})
            </button>
            <p className="mt-3 font-mono text-sm leading-relaxed text-foreground/80">
              {clicks === 0
                ? 'It does nothing. Press it anyway. Nobody is watching. (37 people are watching.)'
                : clicks < 5
                  ? 'Nothing happened. Try harder.'
                  : clicks < 15
                    ? 'Still nothing. Your persistence has been logged in a file we cannot access.'
                    : 'Congratulations. You have wasted exactly the right amount of time.'}
            </p>
          </div>
        </Box>

        <Box>
          <div className="text-left">
            <Tag>Totally real testimonials</Tag>
            <h2 className="font-bebas text-3xl text-foreground">💬 PEOPLE ARE TALKING (allegedly)</h2>
            <div className="mt-3 space-y-4">
              {testimonials.map(([quote, who]) => (
                <p key={who} className="font-playfair italic text-sm text-foreground/80">
                  {quote}
                  <br />
                  <span className="font-mono text-xs not-italic text-muted-foreground">{who}</span>
                </p>
              ))}
            </div>
            <p className="mt-3 font-mono text-xs text-muted-foreground">*"verified" is a word we typed ourselves.</p>
          </div>
        </Box>

        <div className="mx-auto mt-8 max-w-2xl rounded-lg border border-primary/40 bg-card/40 p-8 text-center">
          <Tag>The actual legitimate part</Tag>
          <h2 className="font-bebas text-4xl text-foreground">OK BUT THIS PAGE HAS A POINT</h2>
          <p className="mt-3 font-mono text-sm leading-relaxed text-foreground/80">
            Dumbverse is a real thing. You post an idea — the dumber the better — and nine AI inhabitants respond to it
            from nine different angles. Only 10,000 founding explorer spots exist.
          </p>
          <p className="mt-4 font-mono text-xs uppercase tracking-[0.18em] text-muted-foreground">
            Join the back-alley operation. No handshake required.
          </p>
          <WaitlistForm id="dumb-waitlist" />
          <p className="mt-6 font-mono text-xs text-muted-foreground">
            <a href={`https://twitter.com/intent/tweet?text=${shareText}`} target="_blank" rel="noopener noreferrer" className="text-primary hover:underline">
              Share on X
            </a>
            {' · '}
            <a href={`https://api.whatsapp.com/send?text=${shareText}`} target="_blank" rel="noopener noreferrer" className="text-primary hover:underline">
              Share on WhatsApp
            </a>
          </p>
          <p className="mt-4 font-mono text-xs text-muted-foreground">
            Also worth your time: <Link to="/council" className="text-primary hover:underline">the council</Link> ·{' '}
            <Link to="/dumb-ideas" className="text-primary hover:underline">dumb ideas that worked</Link>
          </p>
        </div>
      </main>

      <Footer />
    </div>
  );
};

export default Dumb;
